import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Alert,
  Snackbar,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { MenuItem, Select, InputLabel } from "@mui/material";
import axios from "axios";
import { env } from "../../../utilities/function";
import { getCookie } from "../../../utilities/cookies";

const API_BASE_URL = env("SERVER") + "/api";

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const ProductOfferForm = ({ open, onClose, offer, onSuccess }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [plans, setPlans] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    productSlug: "",
    logos: { dark: "", light: "" },
    hero: { tagline: "", title: "", subtitle: "" },
    appType: "hr",
    pricingType: "subscription",
    pricing: [],
    testimonials: [],
    faqs: [],
    bottomCta: {
      title: "",
      subtitle: "",
      buttonText: "Get Started for Free",
      inputPlaceholder: "Enter Your Email",
    },
    isEnabled: true,
  });

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const accessToken = getCookie("accessToken");
        const response = await axios.get(`${API_BASE_URL}/plans?limit=1000`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setPlans(response.data.data.plans || []);
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };
    if (open) fetchPlans();
  }, [open]);

  useEffect(() => {
    if (offer) {
      setFormData({
        ...offer,
        appType: offer.appType || "hr",
        pricingType: offer.pricingType || "subscription",
      });
    } else {
      setFormData({
        name: "",
        slug: "",
        hero: { tagline: "", title: "", subtitle: "" },
        appType: "hr",
        pricingType: "subscription",
        pricing: [],
        testimonials: [],
        faqs: [],
        bottomCta: {
          title: "",
          subtitle: "",
          buttonText: "Get Started for Free",
          inputPlaceholder: "Enter Your Email",
        },
        isEnabled: true,
      });
    }
  }, [offer, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleHeroChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      hero: { ...prev.hero, [field]: value },
    }));
  };

  const handleLogoChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      logos: { ...prev.logos || {}, [field]: value },
    }));
  };

  const handleCtaChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      bottomCta: { ...prev.bottomCta, [field]: value },
    }));
  };

  const handlePricingTypeChange = (e) => {
    const newType = e.target.value;
    setFormData((prev) => ({
      ...prev,
      pricingType: newType,
      pricing: prev.pricing.map((plan) => ({
        ...plan,
        monthlyPrice: newType === "subscription" ? plan.monthlyPrice : 0,
        yearlyPrice: newType === "subscription" ? plan.yearlyPrice : 0,
        oneTimePrice: newType === "one-time" ? plan.oneTimePrice : 0,
      })),
    }));
  };

  // Array Handlers
  const handleAddArrayItem = (field, emptyItem) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], emptyItem],
    }));
  };

  const handleRemoveArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleArrayItemChange = (field, index, subField, value) => {
    setFormData((prev) => {
      const newArray = [...prev[field]];
      newArray[index] = { ...newArray[index], [subField]: value };
      return { ...prev, [field]: newArray };
    });
  };

  const handlePricingFeatureChange = (planIndex, featureIndex, value) => {
    setFormData((prev) => {
      const newPricing = [...prev.pricing];
      const newFeatures = [...newPricing[planIndex].features];
      newFeatures[featureIndex] = value;
      newPricing[planIndex] = { ...newPricing[planIndex], features: newFeatures };
      return { ...prev, pricing: newPricing };
    });
  };

  const addPricingFeature = (planIndex) => {
    setFormData((prev) => {
      const newPricing = [...prev.pricing];
      newPricing[planIndex] = {
        ...newPricing[planIndex],
        features: [...(newPricing[planIndex].features || []), ""],
      };
      return { ...prev, pricing: newPricing };
    });
  };

  const removePricingFeature = (planIndex, featureIndex) => {
    setFormData((prev) => {
      const newPricing = [...prev.pricing];
      newPricing[planIndex] = {
        ...newPricing[planIndex],
        features: newPricing[planIndex].features.filter((_, i) => i !== featureIndex),
      };
      return { ...prev, pricing: newPricing };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const accessToken = getCookie("accessToken");
      const url = offer
        ? `${API_BASE_URL}/product-offers/${offer._id}`
        : `${API_BASE_URL}/product-offers`;
      const method = offer ? "put" : "post";

      await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      onSuccess();
    } catch (error) {
      console.error("Error saving offer:", error);
      setSnackbar({
        open: true,
        message: "Error saving offer: " + (error.response?.data?.message || error.message),
        severity: "error",
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{offer ? "Edit Product Offer" : "Create Product Offer"}</DialogTitle>
        <DialogContent dividers>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab label="General" />
            <Tab label="Hero Section" />
            <Tab label="Pricing" />
            <Tab label="Testimonials" />
            <Tab label="FAQs" />
            <Tab label="Bottom CTA" />
          </Tabs>

          {/* General Tab */}
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Internal Name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isEnabled}
                      onChange={(e) => handleChange("isEnabled", e.target.checked)}
                    />
                  }
                  label="Enabled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>App Type</InputLabel>
                  <Select
                    value={formData.appType || "hr"}
                    label="App Type"
                    onChange={(e) => handleChange("appType", e.target.value)}
                  >
                    <MenuItem value="hr">HR</MenuItem>
                    <MenuItem value="files">Files</MenuItem>
                    <MenuItem value="esign">Esign</MenuItem>
                    <MenuItem value="projects">Projects</MenuItem>
                    <MenuItem value="chat">Chat</MenuItem>
                    <MenuItem value="app">App</MenuItem>
                    <MenuItem value="campaigns">Campaigns</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Offer Slug (e.g., special-deal)"
                  value={formData.slug}
                  onChange={(e) => handleChange("slug", e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Dark Mode Logo URL"
                  value={formData.logos?.dark || ""}
                  onChange={(e) => handleLogoChange("dark", e.target.value)}
                  placeholder="https://..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Light Mode Logo URL"
                  value={formData.logos?.light || ""}
                  onChange={(e) => handleLogoChange("light", e.target.value)}
                  placeholder="https://..."
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Hero Tab */}
          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tagline (Small Text)"
                  value={formData.hero.tagline || ""}
                  onChange={(e) => handleHeroChange("tagline", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Main Title"
                  value={formData.hero.title || ""}
                  onChange={(e) => handleHeroChange("title", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Subtitle"
                  value={formData.hero.subtitle || ""}
                  onChange={(e) => handleHeroChange("subtitle", e.target.value)}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Pricing Tab */}
          <TabPanel value={activeTab} index={2}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Pricing Model</FormLabel>
              <RadioGroup
                row
                aria-label="pricingType"
                name="pricingType"
                value={formData.pricingType || "subscription"}
                onChange={handlePricingTypeChange}
              >
                <FormControlLabel
                  value="subscription"
                  control={<Radio />}
                  label="Subscription (Monthly/Yearly)"
                />
                <FormControlLabel
                  value="one-time"
                  control={<Radio />}
                  label="One-Time Purchase"
                />
              </RadioGroup>
            </FormControl>

            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() =>
                handleAddArrayItem("pricing", {
                  name: "Starter",
                  description: "",
                  users: "1 user",
                  monthlyPrice: 0,
                  yearlyPrice: 0,
                  oneTimePrice: 0,
                  features: [],
                })
              }
              sx={{ mb: 2 }}
            >
              Add Pricing Plan
            </Button>
            {formData.pricing.map((plan, index) => (
              <Box key={index} sx={{  p: 2, mb: 2, borderRadius: 1 }} className="border border-gray-300 dark:border-gray-600">
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle1">Plan {index + 1}</Typography>
                  <IconButton color="error" onClick={() => handleRemoveArrayItem("pricing", index)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Plan Name"
                      value={plan.name}
                      onChange={(e) => handleArrayItemChange("pricing", index, "name", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={plan.description}
                      onChange={(e) => handleArrayItemChange("pricing", index, "description", e.target.value)}
                    />
                  </Grid>

                  {formData.pricingType === "subscription" ? (
                    <>
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <InputLabel>Monthly Plan</InputLabel>
                          <Select
                            value={plan.monthlyPlanId || ""}
                            label="Monthly Plan"
                            onChange={(e) => {
                              const planId = e.target.value;
                              handleArrayItemChange("pricing", index, "monthlyPlanId", planId);
                              if (planId) {
                                const selected = plans.find(p => p._id === planId);
                                if (selected) handleArrayItemChange("pricing", index, "monthlyPrice", selected.amount);
                              }
                            }}
                          >
                            <MenuItem value="">None</MenuItem>
                            {plans
                              .filter((p) => p.interval === "month" && p.type === formData.appType)
                              .map((p) => (
                                <MenuItem key={p._id} value={p._id}>
                                  {p.name} - {p.title} (${p.amount})
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <InputLabel>Yearly Plan</InputLabel>
                          <Select
                            value={plan.yearlyPlanId || ""}
                            label="Yearly Plan"
                            onChange={(e) => {
                              const planId = e.target.value;
                              handleArrayItemChange("pricing", index, "yearlyPlanId", planId);
                              if (planId) {
                                const selected = plans.find(p => p._id === planId);
                                if (selected) handleArrayItemChange("pricing", index, "yearlyPrice", selected.amount);
                              }
                            }}
                          >
                            <MenuItem value="">None</MenuItem>
                            {plans
                              .filter((p) => p.interval === "year" && p.type === formData.appType)
                              .map((p) => (
                                <MenuItem key={p._id} value={p._id}>
                                  {p.name} - {p.title} (${p.amount})
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Monthly Price"
                          type="number"
                          value={plan.monthlyPrice}
                          onChange={(e) =>
                            handleArrayItemChange("pricing", index, "monthlyPrice", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Yearly Price"
                          type="number"
                          value={plan.yearlyPrice}
                          onChange={(e) =>
                            handleArrayItemChange("pricing", index, "yearlyPrice", e.target.value)
                          }
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <InputLabel>One-Time Plan</InputLabel>
                          <Select
                            value={plan.oneTimePlanId || ""}
                            label="One-Time Plan"
                            onChange={(e) => {
                              const planId = e.target.value;
                              handleArrayItemChange("pricing", index, "oneTimePlanId", planId);
                              if (planId) {
                                const selected = plans.find(p => p._id === planId);
                                if (selected) handleArrayItemChange("pricing", index, "oneTimePrice", selected.amount);
                              }
                            }}
                          >
                            <MenuItem value="">None</MenuItem>
                            {plans
                              .filter((p) => p.interval === "one-time" && p.type === formData.appType)
                              .map((p) => (
                                <MenuItem key={p._id} value={p._id}>
                                  {p.name} - {p.title} (${p.amount})
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="One-Time Price"
                          type="number"
                          value={plan.oneTimePrice}
                          onChange={(e) =>
                            handleArrayItemChange("pricing", index, "oneTimePrice", e.target.value)
                          }
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Users Text"
                      value={plan.users}
                      onChange={(e) => handleArrayItemChange("pricing", index, "users", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={plan.isPopular || false}
                          onChange={(e) => handleArrayItemChange("pricing", index, "isPopular", e.target.checked)}
                        />
                      }
                      label="Highlight as Most Popular"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption">Features:</Typography>
                    {plan.features?.map((feature, fIndex) => (
                      <Box key={fIndex} display="flex" gap={1} mb={1}>
                        <TextField
                          fullWidth
                          size="small"
                          value={feature}
                          onChange={(e) => handlePricingFeatureChange(index, fIndex, e.target.value)}
                        />
                        <IconButton size="small" onClick={() => removePricingFeature(index, fIndex)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button size="small" onClick={() => addPricingFeature(index)}>
                      + Add Feature
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </TabPanel>

          {/* Testimonials Tab */}
          <TabPanel value={activeTab} index={3}>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() =>
                handleAddArrayItem("testimonials", {
                  name: "",
                  title: "",
                  quote: "",
                  avatarInitials: "",
                })
              }
              sx={{ mb: 2 }}
            >
              Add Testimonial
            </Button>
            {formData.testimonials.map((t, index) => (
              <Box key={index} sx={{ p: 2, mb: 2, borderRadius: 1 }} className="border border-gray-300 dark:border-gray-600">
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle2">Testimonial {index + 1}</Typography>
                  <IconButton color="error" onClick={() => handleRemoveArrayItem("testimonials", index)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={t.name}
                      onChange={(e) => handleArrayItemChange("testimonials", index, "name", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Title/Role"
                      value={t.title}
                      onChange={(e) => handleArrayItemChange("testimonials", index, "title", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      label="Initials"
                      value={t.avatarInitials}
                      onChange={(e) => handleArrayItemChange("testimonials", index, "avatarInitials", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Quote"
                      value={t.quote}
                      onChange={(e) => handleArrayItemChange("testimonials", index, "quote", e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </TabPanel>

          {/* FAQs Tab */}
          <TabPanel value={activeTab} index={4}>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() =>
                handleAddArrayItem("faqs", {
                  question: "",
                  answer: "",
                })
              }
              sx={{ mb: 2 }}
            >
              Add FAQ
            </Button>
            {formData.faqs.map((faq, index) => (
              <Box key={index} sx={{ p: 2, mb: 2, borderRadius: 1 }} className="border border-gray-300 dark:border-gray-600">
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle2">FAQ {index + 1}</Typography>
                  <IconButton color="error" onClick={() => handleRemoveArrayItem("faqs", index)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  label="Question"
                  value={faq.question}
                  onChange={(e) => handleArrayItemChange("faqs", index, "question", e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Answer"
                  value={faq.answer}
                  onChange={(e) => handleArrayItemChange("faqs", index, "answer", e.target.value)}
                />
              </Box>
            ))}
          </TabPanel>

          {/* Bottom CTA Tab */}
          <TabPanel value={activeTab} index={5}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.bottomCta.title || ""}
                  onChange={(e) => handleCtaChange("title", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subtitle"
                  value={formData.bottomCta.subtitle || ""}
                  onChange={(e) => handleCtaChange("subtitle", e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Button Text"
                  value={formData.bottomCta.buttonText || ""}
                  onChange={(e) => handleCtaChange("buttonText", e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Input Placeholder"
                  value={formData.bottomCta.inputPlaceholder || ""}
                  onChange={(e) => handleCtaChange("inputPlaceholder", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Redirect Link (Optional)"
                  placeholder="https://example.com"
                  value={formData.bottomCta.redirectLink || ""}
                  onChange={(e) => handleCtaChange("redirectLink", e.target.value)}
                  helperText="If provided, the button will redirect to this URL instead of the default signup page."
                />
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            Save Offer
          </Button>
        </DialogActions>
      </form>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ProductOfferForm;
